import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { ChatHeader } from "@/components/chat/ChatHeader.tsx";
import { ChatInput } from "@/components/chat/ChatInput.tsx";
import { ChatMessages } from "@/components/chat/ChatMessages.tsx";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { ChatFooter } from "@/components/chat/ChatFooter.tsx";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { handbackTutorQueryKey, postFlowTutorUnclaim } from "@/api/admin.ts";
import { useEffect, useMemo, useState } from "react";
import { ChatContext } from "@/components/chat/ChatContext.tsx";
import { ChatFeedback } from "@/components/chat/ChatFeedback.tsx";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { HubConnectionState } from "@microsoft/signalr";
import { toastAndError, toastAndWarn } from "@/helpers/toast.ts";
import { useChatStore } from "@/stores/useChatStore.ts";
import { ModalAwardStudentCoins } from "@/components/modal/ModalAwardStudentCoins.tsx";
import {
  getQuizQuestionWithParts,
  getQuizSessionDetails,
  getTopicPathwayQuiz,
} from "@/api/quiz.ts";
import { toast } from "sonner";
import { setExtra } from "@sentry/react";
import { getQuestionIdFromFlowItem } from "@/helpers/questionParts.ts";
import { IsAiTutorId, IsAiTutorUserId } from "@/helpers/aiTutor.ts";
import { isHeldAssignedToMe } from "@/helpers/heldReply.ts";
import { useClaimSession } from "@/helpers/useClaimSession.ts";
import { IsHeldReplyStateHeld } from "@/api/chat.ts";
import { ChatReviewPanel } from "@/components/chat/ChatReviewPanel.tsx";
import { ChatAiAutoSendBar } from "@/components/chat/ChatAiAutoSendSwitch.tsx";
import { useUserStore } from "@/stores/useUserStore.ts";
import { useStateWithDeps } from "use-state-with-deps";
import { log } from "@/helpers/console.ts";
import { useActiveSessionStore } from "@/stores/useActiveSessionStore.ts";

const FlexRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-shrink: 1;
  background-color: ${t.brandSecondary};
  width: stretch;
  overflow-x: scroll;
  scrollbar-width: none;
  height: calc(100dvh - 4rem);

  @container style(--small-height: 1) {
    height: calc(100dvh - 2.5rem);
  }
`;

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 1;
  width: 100%;
  height: 100%;
  min-width: 700px;

  &[data-context="true"] {
    min-width: 680px;
  }

  &[data-full] {
    height: 100%;
  }
`;

const NoChat = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: #999;
`;

const FeedbackWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const Chat = () => {
  const { chats, activeChat, ResetUnreadCounter, SetChatHasBeenOpened, SoftResetChat } =
    useChatStore();

  const openedChat = chats.get(activeChat.unwrapOrElse(() => 0));

  const [showContext, setShowContext] = useStateWithDeps(false, [
    openedChat?.flowGeneratorSessionId,
  ]);
  const [showFeedback, setShowFeedback] = useStateWithDeps(false, [
    openedChat?.flowGeneratorSessionId,
  ]);
  const [showTerminate, _setShowTerminate] = useStateWithDeps(false, [
    openedChat?.flowGeneratorSessionId,
  ]);
  const [showAwardCoins, setShowAwardCoins] = useStateWithDeps(false, [
    openedChat?.flowGeneratorSessionId,
  ]);
  const [reviewAssignments, setReviewAssignments] = useStateWithDeps<Set<number>>(new Set(), [
    openedChat?.flowGeneratorSessionId,
  ]);

  const [isSignalROffline, setIsSignalROffline] = useState(false);
  const [isBrowserOffline, setIsBrowserOffline] = useState(!navigator.onLine);

  const { signalr } = useSignalStore();
  const { userId } = useUserStore();
  const { SetSessionCompleted } = useAssignedChatStore();
  const { assignedSessions } = useAssignedChatStore();

  const assignedSession = assignedSessions.get(openedChat?.flowGeneratorSessionId ?? -1);

  useEffect(() => {
    if (
      !showFeedback &&
      !showTerminate &&
      !showAwardCoins &&
      openedChat?.removeHookCallbacks.length === 0
    )
      SoftResetChat(openedChat.flowGeneratorSessionId);
  }, [
    showFeedback,
    showTerminate,
    openedChat?.removeHookCallbacks,
    openedChat?.flowGeneratorSessionId,
    SoftResetChat,
    showAwardCoins,
  ]);

  const currentTutorUserId = openedChat?.flowGeneratorSession?.tutorUserId;
  const previousTutorId = openedChat?.flowGeneratorSession?.previousTutorId;

  const isAssignmentCompleted = !!assignedSession?.completed;

  // Is this session assigned to anybody
  const isSessionActive = !!currentTutorUserId || !isAssignmentCompleted;

  // Is this session assigned to me
  const isMySession =
    (currentTutorUserId === userId && IsAiTutorId(previousTutorId)) || !!assignedSession;
  // Is this session assigned to me or was the previous tutor the ai-tutor
  const isMyActiveSession = isMySession && !isAssignmentCompleted;

  // Is the current tutor id === ai tutor?
  const isActiveAiSession = IsAiTutorUserId(currentTutorUserId);
  // Was this session previously set to AI Tutor (and not currently set to ai tutor)
  const wasAiSession = !isActiveAiSession && IsAiTutorId(previousTutorId);

  const heldReplyState = openedChat?.flaggedMessage.flagged;
  const heldReplyStateHeld = IsHeldReplyStateHeld(heldReplyState);

  // Round-robin ASSIGNED safety review: the hold handed this session to ME as
  // reviewer, so it registers as "claimed" (tutorUserId === my userId) while
  // the AI stays paused — the review surfaces must still show. Two signals,
  // either suffices: the intervention fingerprint (payloads that carry the
  // contract fields), or my session probing an unresolved hold (works even
  // while the intervention rows lack the fingerprint fields — a genuinely
  // claimed chat has its holds resolved and never probes "held").
  const heldAssignedToMe =
    (isMySession && isHeldAssignedToMe(openedChat?.flowGeneratorSession, userId)) ||
    (!!heldReplyState && heldReplyStateHeld);

  const openedId = openedChat?.flowGeneratorSessionId;
  useEffect(() => {
    if (!openedId) return;

    if (heldAssignedToMe) {
      setReviewAssignments((previous) =>
        previous.has(openedId) ? previous : new Set(previous).add(openedId),
      );
    } else if (!isMySession) {
      setReviewAssignments((previous) => {
        if (!previous.has(openedId)) return previous;
        const next = new Set(previous);
        next.delete(openedId);
        return next;
      });
    }
  }, [openedId, heldAssignedToMe, isMySession, setReviewAssignments]);
  const isReviewAssignment = !!openedId && isMySession && reviewAssignments.has(openedId);

  // A real claim happened — stop treating this ownership as a review
  // assignment so the chat input unlocks
  const clearReviewAssignment = () => {
    if (!openedId) return;
    setReviewAssignments((previous) => {
      if (!previous.has(openedId)) return previous;
      const next = new Set(previous);
      next.delete(openedId);
      return next;
    });
  };

  // Claim from the post-approve limbo bar (assignment is mine, hold resolved)
  const { attemptClaim: claimAssignedSession } = useClaimSession(
    openedId ?? -1,
    undefined,
    clearReviewAssignment,
  );

  const reviewableOwnership =
    openedChat?.flowGeneratorSession !== undefined &&
    (!currentTutorUserId || isActiveAiSession || heldAssignedToMe);

  const reviewableHeldState = openedChat?.flaggedMessage.flagged;

  const flowQuizSession = useQuery({
    queryFn: () => getQuizSessionDetails(openedChat?.flowGeneratorSession?.quizSessionId ?? -1),
    queryKey: ["flow-quiz-session", openedChat?.flowGeneratorSession?.quizSessionId ?? -1],
    enabled: !!(
      openedChat &&
      openedChat.flowGeneratorSession &&
      openedChat.flowGeneratorSession.quizSessionId &&
      openedChat.flowGeneratorSession.quizSessionId &&
      openedChat?.flowGeneratorSession?.quizName !== "Retrieval Practice Quiz"
    ),
    staleTime: 1000 * 60,
  });

  const flowQuiz = useQuery({
    queryFn: () => getTopicPathwayQuiz(flowQuizSession.data?.topicPathwayQuizId ?? -1),
    queryKey: ["flow-quiz-pathway", flowQuizSession.data?.topicPathwayQuizId ?? -1],
    enabled:
      openedChat?.flowGeneratorSessionId !== undefined &&
      openedChat?.flowGeneratorSession !== undefined &&
      openedChat?.flowGeneratorSession?.quizName !== "Retrieval Practice Quiz" &&
      flowQuizSession.data !== undefined,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const questionIds = useMemo(() => {
    const flowItems = [
      ...(openedChat?.flowGeneratorSession?.flowGeneratorSession?.flowItems ?? []),
      ...(openedChat?.newMessages ?? []),
    ];

    return [
      ...new Set(
        flowItems
          .filter((flowItem) => flowItem.flowItemType === "PinnedImage")
          .map(getQuestionIdFromFlowItem)
          .filter((questionId): questionId is number => questionId !== null),
      ),
    ];
  }, [openedChat?.flowGeneratorSession?.flowGeneratorSession?.flowItems, openedChat?.newMessages]);

  const questionPartQueries = useQueries({
    queries: questionIds.map((questionId) => ({
      queryFn: () => getQuizQuestionWithParts(questionId),
      queryKey: ["quiz-question-parts", questionId],
      staleTime: 1000 * 60 * 60,
    })),
  });
  const questionsById = new Map(
    questionPartQueries.flatMap((query, index) =>
      query.data ? [[questionIds[index], query.data] as const] : [],
    ),
  );

  useEffect(() => {
    setExtra("opened-chat-flow-id", openedChat?.flowGeneratorSessionId ?? 0);
    setExtra(
      "active-chat-intervention-id",
      assignedSession ? assignedSession.assignedChat.flowGeneratorSessionInterventionId : "",
    );
  }, [openedChat, assignedSession]);

  useEffect(() => {
    if (!openedChat) return;
    if (openedChat.unreadCount > 0) {
      ResetUnreadCounter(openedChat.flowGeneratorSessionId);
    }

    const { RemoveFlaggedSession } = useActiveSessionStore.getState();
    RemoveFlaggedSession({ flowGeneratorSessionId: openedChat.flowGeneratorSessionId });
  }, [ResetUnreadCounter, openedChat]);

  useEffect(() => {
    if (openedChat && !openedChat?.hasBeenOpened) {
      SetChatHasBeenOpened(openedChat.flowGeneratorSessionId);
    }
  }, [SetChatHasBeenOpened, openedChat]);

  const postUnclaimSession = useMutation({
    mutationFn: (flowId: number) =>
      postFlowTutorUnclaim(flowId, {
        DateEnded: new Date().toISOString(),
      }).then(() => {
        SetSessionCompleted(flowId);
        // Ownership changed — the hand-back probe's answer flips with it
        window.QueryClient.invalidateQueries({
          queryKey: handbackTutorQueryKey(flowId),
        }).catch((e) => {
          toastAndWarn("Could not invalidate handback tutor query", e);
        });
      }),
    onError: (error) => {
      toastAndError("Could not unclaim session", error);
    },
  });

  useEffect(() => {
    if (!signalr) return;

    const unhook = signalr.OnStatusCallback((status) => {
      console.log("SignalR state changed callback", status);
      if (status === HubConnectionState.Connected) {
        setIsSignalROffline(false);
      } else {
        setIsSignalROffline(true);
      }
    });

    return () => {
      unhook();
    };
  }, [signalr]);

  useEffect(() => {
    const online = () => setIsBrowserOffline(false);
    const offline = () => setIsBrowserOffline(true);
    const change = (ev: Event) => console.warn("Network change event", ev);

    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener("change", change);

    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener("change", change);
    };
  });

  const offline = isSignalROffline || isBrowserOffline;

  if (offline) {
    // Only works on chromium and safari
    // https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
    // @ts-expect-error
    const extra = navigator.connection;

    return (
      <ChatWrapper data-full>
        <NoChat>
          <p>
            You are currently offline!
            <br />
            Either your local internet connection is down or the server is experiencing issues.
          </p>
          <table>
            <tbody>
              <tr>
                <td>- {isSignalROffline ? "❌" : "✅"} SignalR</td>
              </tr>
              <tr>
                <td>- {isBrowserOffline ? "❌" : "✅"} Browser</td>
              </tr>
              {extra && (
                <>
                  <tr>
                    <td>- ℹ️ Type: {extra.type}</td>
                  </tr>
                  <tr>
                    <td>- ℹ️ Effective Type: {extra.effectiveType}</td>
                  </tr>
                  <tr>
                    <td>- ℹ️ RTT: {extra.rtt}</td>
                  </tr>
                  <tr>
                    <td>- ℹ️ Downlink: {extra.downlink}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </NoChat>
      </ChatWrapper>
    );
  }

  if (!openedChat) {
    return (
      <ChatWrapper data-full>
        <NoChat>No Tutoring Session Selected</NoChat>
      </ChatWrapper>
    );
  }

  if (!openedChat?.flowGeneratorSession) {
    return (
      <ChatWrapper data-full>
        <NoChat>Loading...</NoChat>
      </ChatWrapper>
    );
  }

  const isHeldForReview = !!(
    openedChat.flowGeneratorSession.isAutoModerated &&
    openedChat.flowGeneratorSession.isAutoResponseEnabled === false &&
    openedChat.flowGeneratorSession.botJoined &&
    wasAiSession
  );

  const isAiSession = isHeldForReview || (isSessionActive && isActiveAiSession);

  log("Currently opened chat data:", openedChat);

  return (
    <FlexRow className={"layout-item"}>
      <ChatWrapper data-context={showContext}>
        <ChatHeader
          quizShortCode={flowQuiz.data?.shortCode}
          chat={openedChat}
          activeSession={assignedSession}
          showContext={showContext}
          toggleShowContext={() => {
            setShowContext(!showContext);
            if (!showContext) {
              window.dispatchEvent(new CustomEvent("dragbar-reset", { bubbles: true }));
            }
          }}
          isAiSession={isAiSession}
        />
        <ChatMessages
          session={assignedSession}
          chat={openedChat}
          questionsById={questionsById}
          shouldShowPreview={!isAiSession}
        />
        {isSessionActive &&
          isHeldForReview &&
          (!isMySession || heldAssignedToMe) &&
          reviewableOwnership &&
          heldReplyStateHeld && (
            <>
              <ChatReviewPanel
                // Keys force a per-session remount; each sibling needs a DISTINCT
                // key — duplicate keys among siblings make React duplicate and/or
                // orphan the nodes on re-render (stacked bars, ghost panels)
                key={`review-${openedChat.flowGeneratorSessionId}`}
                flowGeneratorSessionId={openedChat.flowGeneratorSessionId}
                // @ts-ignore
                heldState={reviewableHeldState}
                onResolved={() => {}}
                onClaimed={clearReviewAssignment}
              />
            </>
          )}
        {isSessionActive && (isActiveAiSession || isHeldForReview) && (
          <ChatAiAutoSendBar
            key={`ai-auto-send-${openedChat.flowGeneratorSessionId}`}
            flowGeneratorSessionId={openedChat.flowGeneratorSessionId}
            aiOwned={isActiveAiSession}
            heldState={reviewableHeldState ?? undefined}
            isInReview={isHeldForReview}
            onClaim={() => {
              claimAssignedSession(true);
            }}
          />
        )}
        {((assignedSession && isAssignmentCompleted) || !isHeldForReview) && (
          <>
            <ChatInput
              key={`input-${openedChat.flowGeneratorSessionId}`}
              assignedSession={assignedSession}
              chat={openedChat}
              // A held assignment is NOT a claim: while reviewing, the AI still
              // runs the chat — the reviewer acts only through the approval banner
              // (approve/edit, or "Claim instead" to take over) and cannot type in
              // the chat until they actually claim
              sessionClaimed={isMyActiveSession && !isReviewAssignment}
              canAwardCoins={!!assignedSession}
              toggleAwardCoins={() => {
                setShowAwardCoins((show) => !show);
              }}
            />
            <ChatFooter
              chat={openedChat}
              assignedSession={assignedSession}
              completeCallback={(unlock, flowGeneratorSessionId) => {
                if (assignedSession && !assignedSession.completed) {
                  const toastId = toast.message("Unclaiming session...", {
                    duration: Infinity,
                    closeButton: true,
                  });
                  postUnclaimSession.mutateAsync(flowGeneratorSessionId).finally(() => {
                    toast.dismiss(toastId);
                  });
                }

                setShowFeedback(true);
                setShowContext(false);
                unlock();
              }}
              openFeedbackCallback={() => {
                setShowFeedback(true);
              }}
              isAiSession={isAiSession}
            />
          </>
        )}
        {showFeedback && (
          <FeedbackWrapper>
            <ChatFeedback
              chat={openedChat}
              interventionId={assignedSession?.assignedChat.flowGeneratorSessionInterventionId ?? 0}
              closeFeedback={() => {
                setShowFeedback(false);
              }}
              flowQuizSession={flowQuizSession.data}
            />
          </FeedbackWrapper>
        )}
      </ChatWrapper>
      {showContext && (
        <ChatContext
          chat={openedChat}
          activeSession={assignedSession}
          quizDataSession={flowQuizSession.data}
          quizShortCode={flowQuiz.data?.shortCode}
          onClose={() => {
            setShowContext(false);
          }}
          questionsById={questionsById}
        />
      )}
      {openedChat.flowGeneratorSession && (
        <ModalAwardStudentCoins
          flowGeneratorSessionId={openedChat.flowGeneratorSessionId}
          isOpen={showAwardCoins}
          onClose={() => {
            setShowAwardCoins(false);
          }}
          studentUserId={openedChat.flowGeneratorSession.userId}
        />
      )}
    </FlexRow>
  );
};
